import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProductModifier } from '@/types';
import { useOrganization } from '@/hooks/useOrganization';
import {
  normalizeProductModifier,
  productModifiersService,
  type ProductModifierInput,
} from '@/services/products/productModifiersService';
import { toast as sonnerToast } from 'sonner';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useContext } from 'react';
import { queryKeys } from '@/lib/queryKeys';
import { STALE_TIME_PRODUCTS } from '@/lib/queryCacheConfig';

export const useProductModifiers = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { translator } = useContext(LanguageProviderContext);
  const orgId = currentOrganization?.id ?? '';

  const { data: productModifiers = [], isLoading, error } = useQuery({
    queryKey: queryKeys.productModifiers(orgId),
    queryFn: async () => {
      if (!currentOrganization) return [];
      return productModifiersService.getProductModifiers(currentOrganization.id);
    },
    select: (rows) => rows.map(normalizeProductModifier),
    enabled: !!currentOrganization,
    staleTime: STALE_TIME_PRODUCTS.productModifiers,
  });

  const createModifier = useMutation({
    mutationFn: async (modifier: ProductModifierInput) => {
      if (!currentOrganization) throw new Error('No organization selected');
      return productModifiersService.createProductModifier(modifier, currentOrganization.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productModifiers(orgId) });
      sonnerToast.success(translator('Products.successCreateModifier'));
    },
    onError: (error: any) => { 
      console.error('Error creating product modifier:', error);
      sonnerToast.error(translator('Errors.unexpectedError'), {
        description: error.message,
      });
    }
  });

  const updateModifier = useMutation({
    mutationFn: async (
      modifier: ProductModifierInput & Pick<ProductModifier, 'id' | 'organizationId'>
    ) => {
      return productModifiersService.updateProductModifier(modifier);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productModifiers(orgId) });
      sonnerToast.success(translator('Products.successUpdateModifier'));
    },
    onError: (error: any) => { 
      console.error('Error updating product modifier:', error);
      sonnerToast.error(translator('Errors.unexpectedError'), {
        description: error.message,
      });
    }
  });

  const deleteModifier = useMutation({
    mutationFn: async (modifierId: string) => {
      return productModifiersService.deleteProductModifier(modifierId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productModifiers(orgId) });
      sonnerToast.success(translator('Products.successDeleteModifier'));
    },
    onError: (error: any) => {
      console.error('Error deleting product modifier:', error);
      sonnerToast.error(translator('Errors.unexpectedError'), {
        description: error.message,
      });
    },
  });

  return {
    productModifiers,
    isLoading,
    error,
    createModifier: createModifier.mutate,
    createModifierAsync: createModifier.mutateAsync,
    updateModifier: updateModifier.mutate,
    updateModifierAsync: updateModifier.mutateAsync,
    deleteModifier: deleteModifier.mutate,
    deleteModifierAsync: deleteModifier.mutateAsync,
    isCreating: createModifier.isPending,
    isUpdating: updateModifier.isPending,
    isDeleting: deleteModifier.isPending,
  };
};
